import React, { createContext, useContext } from 'react';
import PropTypes from 'prop-types';

export const CollapseContext = createContext({});

/**
 * The wrapper that contains the state of all collapsible Content inner components
 */
class Collapse extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            collapseList: {},
            altChildren: null,
            bulkCollapseState: undefined
        };
    }

    registerCollapse = id => {
        const defaultValue = this.state.bulkCollapseState ?? this.props.collapseByDefault;
        this.setState(state => {
            const collapseList = {
                ...state.collapseList,
                [id]: defaultValue
            };
            return {
                collapseList
            };
        });
    };

    getCollapse = id => {
        return this.state.collapseList[id];
    };

    setCollapse = (id, value) => {
        this.setState(state => {
            const collapseList = {
                ...state.collapseList,
                [id]: value
            };
            return {
                collapseList
            };
        });
    };

    toggleCollapse = id => {
        const value = this.getCollapse(id);
        this.setCollapse(id, !value);
    };

    collapseAll = () => {
        this._setValueToAll(true);
    };

    expandAll = () => {
        this._setValueToAll(false);
    };

    _setValueToAll = value => {
        const {collapseList} = this.state;
        const newState = Object.keys(collapseList).reduce((acc, key) => {
            acc[key] = value;
            return acc;
        }, {});
        this.setState({
            collapseList: newState,
            bulkCollapseState: value
        });
    };

    setChildren = () => {
        this.setState({
            altChildren:
                typeof this.props.children === 'function'
                    ? this.props.children({
                        getCollapse: this.getCollapse,
                        setCollapse: this.setCollapse,
                        toggleCollapse: this.toggleCollapse,
                        collapseAll: this.collapseAll,
                        expandAll: this.expandAll
                    })
                    : this.props.children
        });
    }

    componentDidMount() {
        this.setChildren()
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevProps.children !== this.props.children) {
            this.setChildren()
        }
    }

    render() {
        const {collapseByDefault, collapseClassName, showClassName} = this.props;
        const {bulkCollapseState} = this.state;
        return (
            <CollapseContext.Provider
                value={{
                    registerCollapse: this.registerCollapse,
                    getCollapse: this.getCollapse,
                    setCollapse: this.setCollapse,
                    toggleCollapse: this.toggleCollapse,
                    collapseAll: this.collapseAll,
                    expandAll: this.expandAll,
                    bulkCollapseState,
                    collapseByDefault,
                    collapseClassName,
                    showClassName
                }}
            >
                {this.state.altChildren}
            </CollapseContext.Provider>
        );
    }
}

Collapse.propTypes = {
    /**
     * If children is a function it receives as argument an object with the following properties
     * @params {function} props.getCollapse - get the collapse value of specified id
     * @params {function} props.setCollapse - Set the given value of a specified id
     * @params {function} props.toggleCollapse - Toggle the collapse value of a specified id
     * @params {function} props.collapseAll - Collapse all Content inside this wrapper
     * @params {function} props.expandAll - Expand all Content inside this wrapper
     */
    children: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node
    ]),
    collapseByDefault: PropTypes.bool,
    collapseClassName: PropTypes.string,
    showClassName: PropTypes.string
};

Collapse.defaultProps = {
    collapseByDefault: false,
    collapseClassName: 'collapse',
    showClassName: 'show'
};

/**
 * The single content to collapse
 */
function Content({ children, id, className }) {
    const context = useContext(CollapseContext);
    const [altChildren, setAltChildren] = React.useState();
    const collapse = context.getCollapse(id);
    const setCollapse = value => {
        context.setCollapse(id, value);
    };
    const toggleCollapse = () => {
        context.toggleCollapse(id, !collapse);
    };

    if (typeof collapse === 'undefined') {
        context.registerCollapse(id);
    }

    const effectiveCollapse =
        collapse ?? context.bulkCollapseState ?? context.collapseByDefault;
    const classes = [...[context.collapseClassName], ...className.split(' ')];
    if (!effectiveCollapse) classes.push(context.showClassName);

    React.useEffect(() => {
        if (typeof children === 'function') {
            setAltChildren(children({ collapse, setCollapse, toggleCollapse }));
        } else {
            setAltChildren(children);
        }
    }, [children]);

    let allClasses = classes.join(' ');

    return <div className={allClasses}>{altChildren}</div>;
}

Content.propTypes = {
    className: PropTypes.string,
    /**
     * If children is a function it receives as argument an object with the following properties
     * @params {boolean} props.collapse - true if the component is collapsed
     * @params {function} props.setCollapse - Set the collapse to a specified value
     * @params {function} props.toggleCollapse - Toggle the collapse value
     */
    children: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node
    ]),
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired
};

Content.defaultProps = {
    className: ''
};

/**
 * Utility wrapper to switch the collapse status of a specified id
 */
function Toggle({ targetId, children }) {
    const context = useContext(CollapseContext);
    const toggle = () => {
        context.toggleCollapse(targetId);
    };
    return <span onClick={toggle}>{children}</span>;
}

Toggle.propTypes = {
    children: PropTypes.any,
    targetId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        .isRequired
};

Collapse.Toggle = Toggle;
Collapse.Content = Content;

export default Collapse;
